import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { PrimaryButton } from './PrimaryButton';
import { FloatingLabelInput } from './FloatingLabelInput';
import { customerApiErrorMessage, getMe, login } from '../../lib/customer-api';
import { patchCustomer, setCustomerAuth } from '../../lib/customer-auth';
import { toast } from '../../lib/toast';
import { pendingBookingResumePath } from '../../lib/booking-draft';

export function SignInForm() {
  const navigate = useNavigate();
  const routerLoc = useLocation();
  // Where to send the customer after a successful sign-in. Booking
  // pages (and the future /your-appointment, /continue-booking) push
  // `state.from = "/<that path>"` when they redirect a guest here, so
  // the customer lands back on the step that asked them to sign in
  // — not the generic /user-landing. Only honour internal paths
  // (start with "/", not "//") and reject `/professional/*` so a
  // misuse can never bounce a customer signin into the pro app — the
  // resume is strictly for the customer booking flow.
  const fromState = (routerLoc.state as { from?: unknown } | null)?.from;
  const safeFrom =
    typeof fromState === 'string' &&
    fromState.startsWith('/') &&
    !fromState.startsWith('//') &&
    !fromState.startsWith('/professional')
      ? fromState
      : null;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [errors, setErrors] = useState({
    email: false,
    password: false,
  });
  const [submitting, setSubmitting] = useState(false);

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSignIn = async () => {
    const newErrors = {
      email: !email || !validateEmail(email),
      password: !password,
    };

    setErrors(newErrors);

    if (Object.values(newErrors).some((e) => e)) return;

    setSubmitting(true);
    try {
      const res = await login({
        email: email.trim().toLowerCase(),
        password,
      });
      setCustomerAuth(res.accessToken, res.user);
      toast.success('Welcome back!');

      // Refresh authoritative state from /customer/me so we route the
      // user from server-truth rather than whatever was cached in the
      // login response. Important for any field that can change
      // between sessions (verification state, status, etc.).
      let emailVerifiedAt = res.user.email_verified_at;
      try {
        const me = await getMe();
        const info: any = me?.info ?? {};
        const u = info.user ?? info;
        if (u?.email_verified_at !== undefined) {
          emailVerifiedAt = u.email_verified_at;
        }
        patchCustomer({ email_verified_at: emailVerifiedAt ?? null });
      } catch {
        // Server unreachable / stale token — fall back to the login
        // response we already have.
      }

      // If the customer hasn't verified their email yet, route to the
      // success/resend page rather than the landing — mirrors the pro
      // flow and prevents them from being stuck on a half-onboarded
      // account. The `from` hint is preserved so once they verify and
      // sign in again they still land back where they started.
      if (!emailVerifiedAt) {
        navigate('/signup/success', {
          state: { email: res.user.email, from: safeFrom },
        });
      } else {
        // Resume an in-flight booking: prefer the page that bounced the
        // guest here (`safeFrom`), then the persisted booking draft (so
        // an email-verification round-trip that lost the router state
        // still returns to checkout), then the landing page.
        navigate(safeFrom ?? pendingBookingResumePath() ?? '/user-landing');
      }
    } catch (err) {
      toast.error(
        customerApiErrorMessage(err, 'Could not sign in — check your details.'),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full flex flex-col items-center max-w-[420px] mx-auto">
      {/* Title */}
      <div className="text-center mb-12">
        <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] !text-[25px] md:!text-[34px] leading-[32px] md:leading-[48px] mb-2">
          Sign In to your account
        </h1>
        <p className="font-['DM_Sans',sans-serif] font-normal leading-[21px] text-[#908e8e] text-[14px] tracking-[-0.1px]">
          Enter your details to proceed further
        </p>
      </div>

      {/* Form Container */}
      <div className="w-full flex flex-col gap-6">
        <FloatingLabelInput
          label="Email"
          type="email"
          value={email}
          onChange={(val) => {
            setEmail(val);
            setErrors({ ...errors, email: false });
          }}
          icon={``}
          className="w-full"
          error={errors.email}
          errorMessage="Please enter a valid email address"
        />

        <FloatingLabelInput
          label="Your password"
          type="password"
          value={password}
          onChange={(val) => {
            setPassword(val);
            setErrors({ ...errors, password: false });
          }}
          icon={password.length >= 8 ? `` : ``}
          className="w-full"
          error={errors.password}
          errorMessage="Password is required"
        />

        {/* Remember me & Recover password */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => setRememberMe(!rememberMe)}
            className="flex items-center gap-[10px] cursor-pointer group"
          >
            <div className={`size-[20px] rounded-full flex items-center justify-center transition-colors border ${rememberMe ? 'bg-[#8e2a2f] border-[#8e2a2f]' : 'bg-white border-[#eee]'
              }`}>
              {rememberMe && (
                <span className="text-white text-[12px]">✓</span>
              )}
            </div>
            <span className="font-['DM_Sans',sans-serif] font-bold leading-[21px] text-[#3a3335] text-[14px] tracking-[-0.1px]">
              Remember me
            </span>
          </button>

          <Link
            to="/forgot-password"
            className="font-['DM_Sans',sans-serif] font-bold leading-[21px] text-[#8e2a2f] text-[14px] tracking-[-0.1px] hover:underline"
          >
            Recover password
          </Link>
        </div>

        <PrimaryButton
          onClick={handleSignIn}
          className="w-full py-4 mt-2"
          disabled={submitting}
        >
          {submitting ? 'Signing in…' : 'Sign In'}
        </PrimaryButton>

        {/* New-user link — keeps signup reachable from the signin page
            so visitors don't have to navigate back through the role
            chooser. Pairs with the same affordance on ProSignInForm. */}
        <div className="text-center mt-4">
          <p className="font-['DM_Sans',sans-serif] text-[14px] text-[#908e8e]">
            Don't have an account?{' '}
            <Link
              to="/signup/customer"
              state={safeFrom ? { from: safeFrom } : undefined}
              className="text-[#8e2a2f] font-bold hover:underline"
            >
              Create one
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
